import React, { useCallback, useEffect } from 'react';

import { twMerge } from 'tailwind-merge';

import useDeleteFreelancerInsuranceDocument from 'state/remote/remote-freelancer/use-delete-freelancer-insurance-document';

import Typography from 'components-new/typography/typography';
import Divider from 'components-new/divider/divider';
import Button from 'components-new/button/button';

import Icon from 'components/icon/icon';
import Dialog from 'components/dialog/dialog';

import { createLocale } from 'locales';

const globalLocalize = createLocale('global');
const localize = createLocale('freelancerInsuranceInformationPageLocales');

interface DeleteInsuranceDocumentDialogProps {
  isOpen?: boolean;
  onCloseClicked: () => void;
  currentDocumentId: string;
  documentName: string;
  onDeleteSuccess?: () => void;
  onDeleteError?: (error: Error) => void;
}
const DeleteInsuranceDocumentDialog: React.FC<
  DeleteInsuranceDocumentDialogProps
> = (props) => {
  const {
    isOpen,
    onCloseClicked,
    currentDocumentId,
    documentName,
    onDeleteSuccess,
    onDeleteError,
  } = props;

  const { deleteInsuranceDocument, error, isLoading, isSuccess } =
    useDeleteFreelancerInsuranceDocument();

  const closeButtonClassNames = twMerge(
    'absolute right-4 p-2 rounded-md',
    'hover:bg-gray100',
    'transition ease-in-out duration-300',
  );

  useEffect(() => {
    if (error) onDeleteError?.(error);
    if (isSuccess) onDeleteSuccess?.();
    // eslint-disable-next-line
  }, [error, isSuccess]);

  const onConfirm = useCallback(() => {
    if (currentDocumentId !== '') deleteInsuranceDocument(currentDocumentId);
    onCloseClicked();
  }, [deleteInsuranceDocument, currentDocumentId, onCloseClicked]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCloseClicked}
      className="max-w-[95%] max-h-[90%] lg:max-w-[640px]"
    >
      <div
        id="header"
        className="relative flex justify-between items-center px-8 py-4"
      >
        <Typography variant="heading-1" as="p" className="text-gray800">
          {localize('removeDocument')}
        </Typography>
        <button
          type="button"
          className={closeButtonClassNames}
          onClick={onCloseClicked}
        >
          <Icon icon="close" />
        </button>
      </div>
      <Divider orientation="horizontal" className="w-full" />
      <div className="p-4">
        <Typography
          as="h3"
          variant="heading-1"
          className="text-center text-gray700 w-full flex justify-center items-center mt-6"
        >
          {localize('wantToContinue')}
        </Typography>
        <Typography
          as="p"
          variant="body-large-bold"
          className="text-gray500 text-center w-full flex justify-center items-center mt-6"
        >
          {localize('confirmDeleteMessage', { docName: documentName })}
        </Typography>
      </div>
      <Divider orientation="horizontal" className="w-full" />
      <div
        id="footer"
        className="w-full flex justify-between px-3 md:px-auto md:justify-end items-center md:pr-8 py-4"
      >
        <Button
          variant="secondary"
          labelClassName="text-gray500 text-body-medium-bold md:text-body-large-bold"
          className="text-gray500 px-3 md:mr-4 md:px-4 py-2"
          type="button"
          label={globalLocalize('goBack')}
          onClick={onCloseClicked}
        />
        <Button
          loading={isLoading}
          variant="danger"
          className="px-3 lg:px-4 py-2"
          labelClassName="text-body-medium-bold md:text-body-large-bold"
          label={localize('yesRemoveDocument')}
          type="button"
          onClick={onConfirm}
        />
      </div>
    </Dialog>
  );
};

export default DeleteInsuranceDocumentDialog;
